import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Button, Col, Menu, Popover, Row, Slider, Tooltip } from 'antd';
import classNames from 'classnames';
import { VideoJsPlayer } from 'video.js';
import { LoadingOutlined } from '@ant-design/icons';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { useVideoPlayerState } from './hooks/useVideoPlayerState';
import { useSelectedMVComment, useAssetMVPermissions } from '@hooks';
import { checkCanPlaySource } from '@helpers/checkCanPlaySource';
import {
  getFrameAtTime,
  getTimeAtFrame,
  getVideoFrameRate,
  isAudioVideo
} from '@helpers/avInfo';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import AnnotationShortcutHint from '@pages/MediaViewer/AssetViewer/VideoCard/components/ControlBar/AnnotationShortcutHint/AnnotationShortcutHint';
import AssetInfo from '@pages/MediaViewer/AssetViewer/AssetInfo';
import LottieComponent from '@components/Lottie';
import { LgWidth, MdWidth } from '@components/Responsive';
import ControlBarTimer from '@pages/MediaViewer/AssetViewer/VideoCard/components/ControlBarTimer/ControlBarTimer';
import ControlBarSlider from '@pages/MediaViewer/AssetViewer/VideoCard/components/Slider';
import useKeys from '@pages/MediaViewer/AssetViewer/VideoCard/components/ControlBar/hooks/useKeys';
import { useResponsive } from '@hooks/useResponsive';
import {
  AssetProxyInfoDto,
  AssetVersionItemDto,
  AudioVideoMetadataDto
} from '@api/Api';
import {
  PIN_DRAW_TOOL,
  DEFAULT_DRAW_TOOL,
  isAnnotationDrawTool,
  CURSOR_DRAW_TOOL
} from '@pages/MediaViewer/helpers/drawToolShape';
import {
  PlayerControlBarArrowLeftIcon,
  PlayerControlBarArrowRightIcon,
  PlayerControlBarCollapseIcon,
  PlayerControlBarExpandIcon,
  PlayerControlBarFastForwardIcon,
  PlayerControlBarFastRewindIcon,
  PlayerControlBarKeyboardIcon,
  PlayerControlBarPauseIcon,
  PlayerControlBarPauseWithCirclesIcon,
  PlayerControlBarPlayIcon,
  PlayerControlBarPlayWithCirclesIcon,
  PlayerControlBarRepeatIcon,
  PlayerControlBarVolumeIcon
} from '@icons';
import './ControlBar.less';

const normalizeUrl = (url?: string) => {
  if (!url) return '';
  try {
    const u = new URL(url);
    return `${u.origin}${u.pathname}`;
  } catch {
    return url;
  }
};

const speedMenuItems = [
  {
    key: '0.5',
    label: '0.5x'
  },
  {
    key: '0.75',
    label: '0.75x'
  },
  {
    key: '1',
    label: '1x'
  },
  {
    key: '1.25',
    label: '1.25x'
  },
  {
    key: '1.5',
    label: '1.5x'
  },
  {
    key: '2',
    label: '2x'
  }
];

function ControlBar({
  player,
  asset,
  setSelectedCollapse,
  className
}: {
  player: VideoJsPlayer;
  asset: AssetVersionItemDto;
  setSelectedCollapse?: (collapse: string | null) => void;
  className?: string;
}) {
  const canLeaveComment = useAssetMVPermissions('comment');
  const { isMdWidth } = useResponsive();
  const [isHideMobileController, setIsHideMobileController] =
    useState<boolean>(false);
  const timeMobileControllerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  useEffect(() => {
    return () => {
      if (timeMobileControllerRef.current) {
        clearTimeout(timeMobileControllerRef.current);
      }
    };
  }, []);

  const {
    shapesDrawingTools: { shape },
    playerTime,
    canvasMode,
    selectedCommentId,
    timeFormat,
    selectedVersion,
    isMobileDrawingToolbarOpen,
    selectComment,
    setIsSourceSelected,
    setPlayerTime,
    setTimeFormat,
    isCommentFieldFocused,
    setDrawToolsShape,
    setCanvasMode,
    setDocumentHighlight
  } = useAssetViewerState();

  const isAnnotationDrawing = isAnnotationDrawTool(shape);
  const tooltipTrigger = isMdWidth ? [] : undefined;

  const commentDetails = useSelectedMVComment();

  const { isPaused, volume, currentTime, duration, isExpand, buffered } =
    useVideoPlayerState(player);
  const [isLooped, setIsLooped] = useState<boolean>(false);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);

  const selectedVersionRef = useRef(selectedVersion);
  selectedVersionRef.current = selectedVersion;
  const playerTimeRef = useRef(playerTime);
  playerTimeRef.current = playerTime;
  const selectedCommentIdRef = useRef(selectedCommentId);
  selectedCommentIdRef.current = selectedCommentId;
  const shapeRef = useRef(shape);
  shapeRef.current = shape;
  const resetVideoAnnotationDraftRef = useRef<() => void>(() => undefined);
  resetVideoAnnotationDraftRef.current = () => {
    if (playerTime[1] != null) return;
    setDrawToolsShape(DEFAULT_DRAW_TOOL);
    setCanvasMode('view');
    setDocumentHighlight(null);
    selectComment(null);
  };
  const onFastForward = (value: number) => {
    const currentTime = player.currentTime();
    player.currentTime(currentTime + value);
  };

  const {
    speedRef,
    isReverseRunningRef,
    playPause,
    onReverseSeek,
    onExternalPlaybackRateChange
  } = useKeys({
    duration,
    onFastForward,
    player,
    isPaused,
    setPlaybackSpeed,
    isLooped
  });

  const isMuted = player?.muted?.();
  const effectiveIsPaused = isPaused && !isReverseRunningRef.current;
  const isIdleDrawTool = shape === CURSOR_DRAW_TOOL || shape === PIN_DRAW_TOOL;
  const isAnnotationShortcutHintVisible =
    effectiveIsPaused &&
    canLeaveComment &&
    isIdleDrawTool &&
    !selectedCommentId &&
    !isCommentFieldFocused;
  const isCenterPlayButtonVisible =
    effectiveIsPaused &&
    isIdleDrawTool &&
    currentTime === 0 &&
    !selectedCommentId;
  const isCanvasInteractionMode =
    ((shape !== CURSOR_DRAW_TOOL &&
      (!isMdWidth || isMobileDrawingToolbarOpen)) ||
      !!selectedCommentId) &&
    !isCenterPlayButtonVisible;

  const onPlayPause = (value: boolean) => {
    if (speedRef.current < 0) {
      playPause();
      return;
    }

    if (value) {
      player.play();
      if (!playerTime[1]) {
        resetVideoAnnotationDraftRef.current();
      }
    } else {
      player.pause();
    }
  };
  const onSliderChange = (value: number) => {
    if (speedRef.current < 0) {
      onReverseSeek(value);
      return;
    }
    player.currentTime(value);
  };
  const onVolumeChange = (volume: number) => {
    player.volume(volume);
  };
  const onLoopChange = (value: boolean) => {
    player.loop(value);
    setIsLooped((prev) => !prev);
  };
  const onPlayBackRateChange = (value: number) => {
    onExternalPlaybackRateChange(value);
  };
  const onFullscreenChange = (value: boolean) => {
    if (value) player.requestFullscreen();
    else player.exitFullscreen();
  };

  const qualityItems = useMemo(() => {
    if (!selectedVersion?.metadata) return [];
    if (
      selectedVersion?.metadata.type !== 'video' &&
      selectedVersion?.metadata.type !== 'audio'
    )
      return [];
    const metadata = selectedVersion?.metadata || {};
    const proxies =
      Object.entries(metadata.videoProxies)
        .map(([key, info]: [string, AssetProxyInfoDto]) => ({
          quality: parseInt(key, 10),
          src: info.url,
          status: info.status
        }))
        .filter((x) => x.status !== 'skipped')
        .sort((a, b) => b.quality - a.quality)
        .map((x) => ({
          key: x.src ?? `${x.quality}`,
          label: `${x.quality}p`,
          disabled: !x.src,
          isHD: x.quality >= 720,
          isOriginal: false
        })) || [];
    const result = [...proxies];
    if (checkCanPlaySource(selectedVersion)) {
      result.push({
        key: selectedVersion.sourceUrl,
        label: 'Original',
        disabled: false,
        isHD: proxies.some((x) => x.isHD),
        isOriginal: true
      });
    }
    return result;
  }, [selectedVersion?.metadata]);

  const getCurrentClosedCaption = useCallback((tracks: TextTrackList) => {
    if (!selectedVersion) return undefined;
    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i];
      if (track.mode === 'showing') return track.id;
    }
    return undefined;
  }, []);
  const [currentClosedCaption, setCurrentClosedCaption] = useState(() =>
    getCurrentClosedCaption(player.remoteTextTracks())
  );
  useEffect(() => {
    const tracks = player.remoteTextTracks();
    const onChange = () =>
      setCurrentClosedCaption(getCurrentClosedCaption(tracks));
    tracks.addEventListener('change', onChange);
    return () => {
      tracks.removeEventListener('change', onChange);
    };
  }, [player]);
  const [closedCamptionsMenuVisible, setClosedCaptionsMenuVisible] =
    useState(false);
  const closedCaptionItems = useMemo(() => {
    if (!selectedVersion) return [];
    const metadata = selectedVersion?.metadata as AudioVideoMetadataDto;
    const transcriptionStatus = metadata.transcription.status;
    const menuItems: ItemType[] = [];
    if (transcriptionStatus === 'pending' || transcriptionStatus === 'ready') {
      menuItems.push({
        key: `transcription-${selectedVersion.id}`,
        label: <span style={{ paddingRight: 20 }}>Transcription</span>,
        icon: transcriptionStatus === 'pending' ? <LoadingOutlined /> : null,
        disabled: transcriptionStatus === 'pending',
        onClick: () => {
          const tracks = player.remoteTextTracks();
          const track = tracks.getTrackById(
            `transcription-${selectedVersion.id}`
          );
          if (!track) return;
          track.mode = track.mode === 'showing' ? 'hidden' : 'showing';
          const persistenceKey = `active-cc-${selectedVersion.id}`;
          if (track.mode === 'showing') {
            localStorage.setItem(persistenceKey, track.id);
          } else {
            localStorage.removeItem(persistenceKey);
          }
        }
      });
    }
    return menuItems;
  }, [asset?.id, selectedVersion, currentClosedCaption]);

  const currentQuality = qualityItems.find(
    (x) => normalizeUrl(x.key) === normalizeUrl(player.src())
  );

  const videoFrameRate = useMemo(() => {
    if (!selectedVersion || !isAudioVideo(selectedVersion.metadata)) {
      return undefined;
    }
    return getVideoFrameRate(selectedVersion.metadata);
  }, [selectedVersion]);

  const onChangeQuality = (src: string) => {
    if (!selectedVersion) return;
    const metadata = selectedVersion.metadata as AudioVideoMetadataDto;
    const currentTime = player.currentTime();
    const proxy = Object.entries(metadata.videoProxies)
      .map(([key, value]) => ({ key, value }))
      .find((el: any) => el.value.url === src);
    player.src({
      src,
      type: proxy ? 'video/mp4' : selectedVersion.mimeType
    });
    player.currentTime(currentTime);
    player.play();
  };
  useEffect(() => {
    setIsSourceSelected(!!currentQuality?.isOriginal);
  }, [currentQuality]);

  useEffect(() => {
    if (
      (timeFormat === 'frames' || timeFormat === 'timecode') &&
      !videoFrameRate
    ) {
      setTimeFormat('standard');
    }
  }, [timeFormat, videoFrameRate]);

  useEffect(() => {
    const onPause = () => {
      const ct = player.currentTime();
      const metadata = selectedVersionRef.current?.metadata;
      const frame = getFrameAtTime(ct, metadata);
      const snappedTime =
        frame !== null ? getTimeAtFrame(frame, metadata) ?? ct : ct;
      player.currentTime(snappedTime);
      setIsHideMobileController(false);
      if (timeMobileControllerRef.current) {
        clearTimeout(timeMobileControllerRef.current);
        timeMobileControllerRef.current = null;
      }
    };
    const onEnded = () => {
      setIsHideMobileController(false);
      if (timeMobileControllerRef.current) {
        clearTimeout(timeMobileControllerRef.current);
        timeMobileControllerRef.current = null;
      }
    };
    const onFullscreenChange = () => {
      selectComment(null);
    };
    const onPlay = () => {
      setIsHideMobileController(true);
      if (playerTimeRef.current[1] != null) return;
      // Keep draft only while idle (select/comment). Reset annotation drawings on play.
      if (
        selectedCommentIdRef.current !== 'new' &&
        selectedCommentIdRef.current !== 'reply' &&
        !isAnnotationDrawTool(shapeRef.current)
      ) {
        return;
      }
      resetVideoAnnotationDraftRef.current();
    };
    player.on('pause', onPause);
    player.on('play', onPlay);
    player.on('ended', onEnded);
    player.on('fullscreenchange', onFullscreenChange);
    return () => {
      player.off('play', onPlay);
      player.off('pause', onPause);
      player.off('ended', onEnded);
      player.off('fullscreenchange', onFullscreenChange);
    };
  }, [player]);

  useEffect(() => {
    if (!player) return;
    if (!commentDetails?.timeCode?.toSeconds && !playerTime[1]) return;
    const onEscKeyup = (e: any) => {
      if (e.key === 'Escape') {
        if (
          (canvasMode === 'edit' ||
            selectedCommentId === 'new' ||
            selectedCommentId === 'reply') &&
          playerTime[1] &&
          playerTime[1] >= 0
        ) {
          setPlayerTime([playerTime[0], null]);
        }
      }
    };

    document.addEventListener('keyup', onEscKeyup);
    return () => document.removeEventListener('keyup', onEscKeyup);
  }, [player, commentDetails, selectedCommentId, playerTime, canvasMode]);

  useEffect(() => {
    if (!player) return;
    if (
      playerTime[1] &&
      playerTime[1] >= 0 &&
      (canvasMode === 'edit' ||
        selectedCommentId === 'new' ||
        selectedCommentId === 'reply')
    ) {
      const rangeStart = playerTime[0] as number;
      const rangeEnd = playerTime[1] as number;
      const edgeEpsilon = 0.02;

      if (currentTime < rangeStart - edgeEpsilon) {
        if (!isLooped) player.pause();
        player.currentTime(rangeStart);
      }

      if (currentTime > rangeEnd + edgeEpsilon) {
        if (!isLooped) player.pause();
        player.currentTime(rangeStart);
      }
      return;
    }
    if (!commentDetails?.timeCode?.toSeconds) return;

    const commentStart = commentDetails.timeCode.fromSeconds;
    const commentEnd = commentDetails.timeCode.toSeconds;
    const edgeEpsilon = 0.02;

    if (currentTime < commentStart - edgeEpsilon)
      player.currentTime(commentStart);
    if (currentTime > commentEnd + edgeEpsilon) {
      if (!isLooped) player.pause();
      player.currentTime(commentStart);
    }
  }, [player, commentDetails, currentTime, isLooped, canvasMode, playerTime]);

  useEffect(() => {
    if (!player || !commentDetails?.timeCode) return;
    player.currentTime(commentDetails.timeCode.fromSeconds);
    player.pause();
  }, [player, commentDetails]);

  const getFasForwardValue = useCallback(() => {
    if (timeFormat === 'frames' && videoFrameRate) {
      return isPaused ? getTimeAtFrame(1, selectedVersion?.metadata) ?? 1 : 1;
    }
    return 5;
  }, [isPaused, timeFormat, videoFrameRate, selectedVersion?.metadata]);

  const onShowControlController = () => {
    if (timeMobileControllerRef.current) {
      clearTimeout(timeMobileControllerRef.current);
      timeMobileControllerRef.current = null;
    }
    setIsHideMobileController(false);
    timeMobileControllerRef.current = setTimeout(() => {
      setIsHideMobileController(true);
    }, 3000);
  };

  const keyboardMenuItems = [
    {
      key: 'play-pause',
      label: (
        <Row align="middle" gutter={8}>
          <Col>
            <div className="media-viewer-tooltip-label">Space</div>
          </Col>
          <Col>Play / Pause</Col>
        </Row>
      )
    },
    {
      key: 'fullscreen',
      label: (
        <Row align="middle" gutter={8}>
          <Col>
            <div className="media-viewer-tooltip-label">F</div>
          </Col>
          <Col>Enter / Exit Fullscreen</Col>
        </Row>
      )
    },
    {
      key: 'backward',
      label: (
        <Row align="middle" gutter={8}>
          <Col>
            <div className="media-viewer-tooltip-label">J</div>
          </Col>
          <Col>Playback backward</Col>
        </Row>
      )
    },
    {
      key: 'forward',
      label: (
        <Row align="middle" gutter={8}>
          <Col>
            <div className="media-viewer-tooltip-label">L</div>
          </Col>
          <Col>Playback forward</Col>
        </Row>
      )
    },
    {
      type: 'divider' as const
    },
    {
      key: 'frame-backward',
      label: (
        <Row align="middle" gutter={8}>
          <Col>
            <Row align="middle" gutter={4}>
              <Col>
                <div className="media-viewer-tooltip-label">,</div>
              </Col>
              <Col>/</Col>
              <Col>
                <div className="media-viewer-tooltip-label">.</div>
              </Col>
            </Row>
          </Col>
          <Col>Previous / Next frame</Col>
        </Row>
      )
    },
    {
      key: 'rewind-forward-5s',
      label: (
        <Row align="middle" gutter={8}>
          <Col>
            <Row align="middle" gutter={4}>
              <Col>
                <div className="media-viewer-tooltip-label">
                  <PlayerControlBarArrowLeftIcon />
                </div>
              </Col>
              <Col>/</Col>
              <Col>
                <div className="media-viewer-tooltip-label">
                  <PlayerControlBarArrowRightIcon />
                </div>
              </Col>
            </Row>
          </Col>
          <Col>Rewind / Forward 5s</Col>
        </Row>
      )
    },
    {
      type: 'divider' as const
    },
    {
      key: 'mute-unmute',
      label: (
        <Row align="middle" gutter={8}>
          <Col>
            <div className="media-viewer-tooltip-label">M</div>
          </Col>
          <Col>Mute / Unmute</Col>
        </Row>
      )
    },
    {
      key: 'volume-up-down',
      label: (
        <Row align="middle" gutter={8}>
          <Col>
            <Row align="middle" gutter={4}>
              <Col>
                <div className="media-viewer-tooltip-label">-</div>
              </Col>
              <Col>/</Col>
              <Col>
                <div className="media-viewer-tooltip-label">=</div>
              </Col>
            </Row>
          </Col>

          <Col>Increase / Decrease volume</Col>
        </Row>
      )
    },
    {
      type: 'divider' as const
    },
    {
      key: 'previous-visual-comment',
      label: (
        <Row align="middle" gutter={8}>
          <Col>
            <div className="media-viewer-tooltip-label">Shift + P</div>
          </Col>
          <Col>Previous visual comment</Col>
        </Row>
      )
    },
    {
      key: 'next-visual-comment',
      label: (
        <Row align="middle" gutter={8}>
          <Col>
            <div className="media-viewer-tooltip-label">Shift + N</div>
          </Col>
          <Col>Next visual comment</Col>
        </Row>
      )
    }
  ];

  const playButton = (
    <Tooltip
      title={effectiveIsPaused ? 'Play' : 'Pause'}
      overlayClassName="toggle_favorite_overlay"
      placement="bottom"
      trigger={tooltipTrigger}
    >
      {effectiveIsPaused ? (
        <PlayerControlBarPlayIcon onClick={() => onPlayPause(true)} />
      ) : (
        <PlayerControlBarPauseIcon onClick={() => onPlayPause(false)} />
      )}
    </Tooltip>
  );

  const onMuteToggle = () => {
    if (isMdWidth) {
      player.muted(!isMuted);
      onVolumeChange(isMuted ? 1 : 0);
      return;
    }
    if (isMuted) player.muted(false);
    if (!volume) onVolumeChange(0.5);
    else onVolumeChange(0);
  };

  const volumeIcon = (
    <PlayerControlBarVolumeIcon
      className={classNames(
        volume <= 0.5 && volume > 0
          ? 'half'
          : volume === 0 || isMuted
          ? 'muted'
          : volume > 0.5
          ? 'hight'
          : ''
      )}
      style={{ verticalAlign: 'middle', fontSize: 24 }}
      onClick={onMuteToggle}
    />
  );

  const muteButton = (
    <Tooltip
      title={volume ? 'Mute' : 'Unmute'}
      overlayClassName="toggle_favorite_overlay"
      placement="bottom"
      trigger={tooltipTrigger}
    >
      <span style={{ verticalAlign: 'middle' }}>
        {isMdWidth ? (
          volumeIcon
        ) : (
          <Popover
            getPopupContainer={() =>
              document.querySelector('.video-js') as HTMLElement
            }
            placement="right"
            trigger={['hover']}
            content={
              <Slider
                value={volume * 100}
                tooltipVisible={false}
                onChange={(v) => {
                  onVolumeChange(v / 100);
                }}
              />
            }
            overlayClassName="volume_controller_overlay"
          >
            {volumeIcon}
          </Popover>
        )}
      </span>
    </Tooltip>
  );

  const fastRewindButton = (
    <Tooltip
      title="-5 seconds"
      overlayClassName="toggle_favorite_overlay"
      placement="bottom"
      trigger={tooltipTrigger}
    >
      <PlayerControlBarFastRewindIcon
        onClick={() => {
          const value = getFasForwardValue();
          onFastForward(-value);
        }}
      />
    </Tooltip>
  );

  const fastForwardButton = (
    <Tooltip
      title="+5 seconds"
      overlayClassName="toggle_favorite_overlay"
      placement="bottom"
      trigger={tooltipTrigger}
    >
      <PlayerControlBarFastForwardIcon
        onClick={() => {
          const value = getFasForwardValue();
          onFastForward(+value);
        }}
      />
    </Tooltip>
  );

  const closedCaptionButton = closedCaptionItems.length > 0 && (
    <Popover
      open={closedCamptionsMenuVisible}
      onOpenChange={(open) => setClosedCaptionsMenuVisible(open)}
      placement="topRight"
      trigger={['click']}
      getPopupContainer={() =>
        document.querySelector('.video-js') as HTMLElement
      }
      overlayClassName="control_bar_select_overlay"
      content={
        <Menu
          prefixCls="media_viewer_menu-menu"
          selectedKeys={currentClosedCaption ? [currentClosedCaption] : []}
          items={closedCaptionItems}
          onClick={() => setClosedCaptionsMenuVisible(false)}
        />
      }
    >
      <Tooltip
        title="Closed captions"
        overlayClassName="toggle_favorite_overlay"
        placement="bottom"
        trigger={tooltipTrigger}
      >
        <Button type="text" size="small">
          CC
        </Button>
      </Tooltip>
    </Popover>
  );

  const speedButton = (
    <Popover
      placement="topRight"
      trigger={['click']}
      getPopupContainer={() =>
        document.querySelector('.video-js') as HTMLElement
      }
      overlayClassName="control_bar_select_overlay"
      content={
        <Menu
          prefixCls="media_viewer_menu-menu"
          selectedKeys={[playbackSpeed.toString()]}
          onClick={({ key }) => onPlayBackRateChange(+key)}
          style={{ width: 110 }}
          items={speedMenuItems}
        />
      }
    >
      <Tooltip
        title="Speed"
        overlayClassName="toggle_favorite_overlay"
        placement="bottom"
        trigger={tooltipTrigger}
      >
        <div className="speed">{playbackSpeed}x</div>
      </Tooltip>
    </Popover>
  );

  const qualityButton = asset.metadata.type !== 'audio' && (
    <Popover
      placement="topRight"
      trigger={['click']}
      getPopupContainer={() =>
        document.querySelector('.video-js') as HTMLElement
      }
      overlayClassName="control_bar_select_overlay"
      content={
        <Menu
          prefixCls="media_viewer_menu-menu"
          style={{ width: 130 }}
          selectedKeys={currentQuality ? [currentQuality.key] : []}
          onClick={({ key }) => {
            onChangeQuality(key);
          }}
          items={qualityItems.map((x) => ({
            key: x.key,
            label: x.label,
            disabled: x.disabled
          }))}
        />
      }
    >
      <Tooltip
        title="Quality"
        overlayClassName="toggle_favorite_overlay"
        placement="bottom"
        trigger={tooltipTrigger}
      >
        <div className="quality">
          {currentQuality?.label}
          {!!currentQuality?.isHD && <span className="resolution">HD</span>}
        </div>
      </Tooltip>
    </Popover>
  );

  const repeatButton = (
    <Tooltip
      title="Repeat"
      overlayClassName="toggle_favorite_overlay"
      placement="bottom"
      trigger={tooltipTrigger}
    >
      <PlayerControlBarRepeatIcon
        className={classNames(!isLooped && 'loop_disabled')}
        onClick={() => onLoopChange(!isLooped)}
      />
    </Tooltip>
  );

  const slider = (
    <ControlBarSlider
      asset={asset}
      currentTime={currentTime}
      duration={duration}
      onSliderChange={onSliderChange}
      isExpand={isExpand}
      buffered={buffered}
      setSelectedCollapse={setSelectedCollapse}
    />
  );

  const timer = <ControlBarTimer player={player} duration={duration} />;

  return (
    <>
      <div
        className={classNames(
          (isExpand || isCanvasInteractionMode) &&
            'background_wrapper_expanded',
          isCanvasInteractionMode && 'background_wrapper_canvas-interaction',
          'background_wrapper'
        )}
        onClick={() => {
          if (isCanvasInteractionMode) return;
          onPlayPause(isPaused);
        }}
      >
        {asset.metadata.type === 'audio' && (
          <LottieComponent
            view="audio"
            className="icon-audio"
            isStopped={isPaused}
            autoPlay={false}
          />
        )}
        {isExpand && isPaused && <AssetInfo />}
      </div>
      {!isMdWidth && isCenterPlayButtonVisible && (
        <div
          className="video_control_bar__play-circles"
          onClick={() => onPlayPause(true)}
        >
          <PlayerControlBarPlayWithCirclesIcon />
        </div>
      )}
      {isMdWidth && !isCanvasInteractionMode && !selectedCommentId && (
        <>
          {isHideMobileController ? (
            <div
              className="toggle_controller_mobile"
              onClick={onShowControlController}
            />
          ) : (
            <div className="video_control_bar__controller_mobile">
              {effectiveIsPaused ? (
                <PlayerControlBarPlayWithCirclesIcon
                  onClick={() => onPlayPause(true)}
                />
              ) : (
                <>
                  {fastRewindButton}
                  <PlayerControlBarPauseWithCirclesIcon
                    onClick={() => onPlayPause(false)}
                  />
                  {fastForwardButton}
                </>
              )}
            </div>
          )}
        </>
      )}
      <div
        className={classNames(
          isExpand && 'video_control_bar_wrapper',
          className
        )}
      >
        <div
          className={classNames(
            !isPaused && isExpand && 'video_control_bar_expanded',
            'video_control_bar'
          )}
        >
          <MdWidth>
            <div
              className={classNames('video_control_bar_mobile', {
                'video_control_bar_mobile-shape-drawing': isAnnotationDrawing
              })}
            >
              <Row
                align="middle"
                gutter={8}
                justify="space-between"
                style={{ marginBottom: 16 }}
              >
                <Col>{timer}</Col>
                <Col>{qualityButton}</Col>
              </Row>
              {slider}
              <Row align="middle" gutter={8} justify="space-between">
                <Col>{muteButton}</Col>
                <Col>
                  <div className="video_control_bar_container__controller">
                    {fastRewindButton}
                    {playButton}
                    {fastForwardButton}
                  </div>
                </Col>
                <Col>
                  <div className="video_control_bar_container__options">
                    {closedCaptionButton}
                    {speedButton}
                    {repeatButton}
                  </div>
                </Col>
              </Row>
            </div>
          </MdWidth>
          <LgWidth>
            {isAnnotationShortcutHintVisible && <AnnotationShortcutHint />}
            {slider}
            <div className="video_control_bar_container">
              <div className="video_control_bar_container__controller">
                {playButton}
                {fastRewindButton}
                {fastForwardButton}
                {muteButton}
              </div>
              {timer}
              <div className="video_control_bar_container__options">
                {closedCaptionButton}
                {speedButton}
                {repeatButton}
                {asset.metadata.type !== 'audio' && (
                  <>
                    <Popover
                      placement="topRight"
                      trigger={['click']}
                      getPopupContainer={() =>
                        document.querySelector('.video-js') as HTMLElement
                      }
                      overlayClassName="control_bar_select_overlay"
                      content={
                        <Menu
                          prefixCls="media_viewer_menu-menu"
                          style={{ width: 130 }}
                          selectedKeys={
                            currentQuality ? [currentQuality.key] : []
                          }
                          onClick={({ key }) => {
                            onChangeQuality(key);
                          }}
                          items={qualityItems.map((x) => ({
                            key: x.key,
                            label: x.label,
                            disabled: x.disabled
                          }))}
                        />
                      }
                    >
                      <Tooltip
                        title="Quality"
                        overlayClassName="toggle_favorite_overlay"
                        placement="bottom"
                        trigger={tooltipTrigger}
                      >
                        <div className="quality">
                          {currentQuality?.label}
                          {!!currentQuality?.isHD && (
                            <span className="resolution">HD</span>
                          )}
                        </div>
                      </Tooltip>
                    </Popover>
                    <Tooltip
                      title="Full screen"
                      overlayClassName="toggle_favorite_overlay"
                      placement="bottom"
                      trigger={tooltipTrigger}
                    >
                      {isExpand ? (
                        <PlayerControlBarCollapseIcon
                          onClick={() => onFullscreenChange(false)}
                        />
                      ) : (
                        <PlayerControlBarExpandIcon
                          onClick={() => onFullscreenChange(true)}
                        />
                      )}
                    </Tooltip>
                  </>
                )}
                <Popover
                  placement="topRight"
                  trigger={['hover']}
                  getPopupContainer={() =>
                    document.querySelector('.video-js') as HTMLElement
                  }
                  overlayClassName="control_bar_select_overlay keyboard_menu_overlay"
                  content={
                    <Menu
                      prefixCls="media_viewer_menu-menu"
                      style={{ width: 350 }}
                      items={keyboardMenuItems}
                      selectable={false}
                    />
                  }
                >
                  <PlayerControlBarKeyboardIcon />
                </Popover>
              </div>
            </div>
          </LgWidth>
        </div>
      </div>
    </>
  );
}

export default ControlBar;
